/**
 * DS Coder — resolves the effective rules for a chat by merging built-in
 * Deeply Smart V2 project rules with DB-managed rules (global / workspace /
 * user). Precedence (later wins in prompt order): built-in → global →
 * workspace → user. Disabled rules are dropped. Best-effort + cached: any
 * failure still returns enabled built-ins so a chat is never blocked.
 */
import { BUILTIN_DS_CODER_RULES } from "@/services/dsCoder/builtinProjectRules";
import { listResolvableRules } from "@/services/dsCoderRulesService";
import type { DsCoderRule } from "@/types/dsCoderRules";

const CACHE_TTL_MS = 5 * 60 * 1000;
type CacheEntry = { value: DsCoderRule[]; at: number };
const cache = new Map<string, CacheEntry>();

export function clearDsCoderRulesCache(): void {
  cache.clear();
}

function cacheKey(userId?: string | null, workspaceId?: string | null): string {
  return `${userId ?? "anon"}|${workspaceId ?? "none"}`;
}

/** Higher-precedence scopes sort last so they are applied on top. */
const SCOPE_ORDER: Record<DsCoderRule["scope"], number> = { global: 0, workspace: 1, user: 2 };

function enabledBuiltins(): DsCoderRule[] {
  return BUILTIN_DS_CODER_RULES.filter((r) => r.isEnabled);
}

/**
 * Returns the effective, enabled rules ordered built-in → global → workspace → user.
 * Never throws.
 */
export async function resolveRules(opts: {
  userId?: string | null;
  workspaceId?: string | null;
}): Promise<DsCoderRule[]> {
  const key = cacheKey(opts.userId, opts.workspaceId);
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.value;

  const builtins = enabledBuiltins();
  try {
    const dbRules = await listResolvableRules(opts.workspaceId ?? null);
    const value = [
      ...builtins,
      ...dbRules
        .filter((r) => r.isEnabled)
        .sort((a, b) => SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope]),
    ];
    cache.set(key, { value, at: Date.now() });
    return value;
  } catch (e) {
    console.warn("[DS Coder] Failed to resolve managed rules; using built-ins only:", e);
    cache.set(key, { value: builtins, at: Date.now() });
    return builtins;
  }
}
